import { useCallback, useEffect, useState } from "react";
import { CaptureUpdateAction, useExcalidrawAPI } from "@excalidraw/excalidraw";
import {
  LoadIcon,
  PlusIcon,
  TrashIcon,
  pencilIcon,
} from "@excalidraw/excalidraw/components/icons";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";

import type { FileId } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { useAtom, useAtomValue } from "../app-jotai";
import { isCollaboratingAtom } from "../collab/Collab";
import { LocalData } from "../data/LocalData";
import {
  Workplaces,
  activeWorkplaceIdAtom,
  workplacesAtom,
  type WorkplaceMeta,
} from "../data/Workplaces";

import "./WorkplacesPanel.scss";

import type { MouseEvent as ReactMouseEvent } from "react";

const formatUpdatedAt = (timestamp: number) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toLocaleString();
  }
};

const loadWorkplaceOntoCanvas = async (
  workplaceId: string,
  excalidrawAPI: ExcalidrawImperativeAPI,
) => {
  const data = await Workplaces.load(workplaceId);
  if (!data) {
    return;
  }

  LocalData.flushSave();

  const elements = restoreElements(data.elements, null, {
    repairBindings: true,
    deleteInvisibleElements: true,
  });
  const appState = restoreAppState(
    {
      ...data.appState,
      name: data.name,
    },
    getDefaultAppState(),
  );

  excalidrawAPI.updateScene({
    elements,
    appState,
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
  excalidrawAPI.history.clear();

  const fileIds = elements.reduce((acc, element) => {
    if (isInitializedImageElement(element)) {
      acc.push(element.fileId);
    }
    return acc;
  }, [] as FileId[]);

  if (fileIds.length) {
    const { loadedFiles, erroredFiles } = await LocalData.fileStorage.getFiles(
      fileIds,
    );
    if (loadedFiles.length) {
      excalidrawAPI.addFiles(loadedFiles);
    }
    if (erroredFiles.size) {
      console.warn("Some workplace images failed to load", erroredFiles);
    }
  }

  Workplaces.setActiveId(workplaceId);

  // Keep localStorage in sync immediately so refresh restores this workplace
  LocalData.save(
    excalidrawAPI.getSceneElementsIncludingDeleted(),
    excalidrawAPI.getAppState(),
    excalidrawAPI.getFiles(),
    () => {},
  );
  LocalData.flushSave();
};

export const WorkplacesPanel = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const workplaces = useAtomValue(workplacesAtom);
  const [activeId, setActiveId] = useAtom(activeWorkplaceIdAtom);
  const isCollaborating = useAtomValue(isCollaboratingAtom);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Workplaces.refreshIndex().catch(console.error);
    const stored = Workplaces.getActiveId();
    if (stored) {
      setActiveId(stored);
    }
  }, [setActiveId]);

  const withBusy = useCallback(
    async (fn: () => Promise<void>) => {
      if (busy) {
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await fn();
      } catch (err: any) {
        console.error(err);
        setError(err?.message || "Something went wrong");
      } finally {
        setBusy(false);
      }
    },
    [busy],
  );

  const handleOpen = (workplace: WorkplaceMeta) => {
    if (!excalidrawAPI || workplace.id === activeId || isCollaborating) {
      return;
    }
    withBusy(async () => {
      await loadWorkplaceOntoCanvas(workplace.id, excalidrawAPI);
    });
  };

  const handleCreate = () => {
    if (!excalidrawAPI || isCollaborating) {
      return;
    }
    withBusy(async () => {
      LocalData.flushSave();
      const created = await Workplaces.create({
        name: `Untitled ${workplaces.length + 1}`,
        elements: [],
        appState: {
          ...getDefaultAppState(),
          theme: excalidrawAPI.getAppState().theme,
          viewBackgroundColor: excalidrawAPI.getAppState().viewBackgroundColor,
        },
      });
      await loadWorkplaceOntoCanvas(created.id, excalidrawAPI);
    });
  };

  const startRename = (workplace: WorkplaceMeta, event: ReactMouseEvent) => {
    event.stopPropagation();
    setEditingId(workplace.id);
    setDraftName(workplace.name);
  };

  const commitRename = (id: string) => {
    const name = draftName;
    setEditingId(null);
    withBusy(async () => {
      await Workplaces.rename(id, name);
      if (excalidrawAPI && id === Workplaces.getActiveId()) {
        excalidrawAPI.updateScene({
          appState: { name: name.trim() || "Untitled" },
          captureUpdate: CaptureUpdateAction.NEVER,
        });
      }
    });
  };

  const handleDelete = (workplace: WorkplaceMeta, event: ReactMouseEvent) => {
    event.stopPropagation();
    if (workplaces.length <= 1 || isCollaborating) {
      return;
    }
    const confirmed = window.confirm(
      `Delete workplace "${workplace.name}"? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    withBusy(async () => {
      const wasActive = workplace.id === Workplaces.getActiveId();
      const ok = await Workplaces.remove(workplace.id);
      if (!ok) {
        setError("Keep at least one workplace.");
        return;
      }
      if (wasActive && excalidrawAPI) {
        const nextId = Workplaces.getActiveId();
        if (nextId) {
          await loadWorkplaceOntoCanvas(nextId, excalidrawAPI);
        }
      }
    });
  };

  return (
    <div className="workplaces-panel">
      <div className="workplaces-panel__header">
        <div className="workplaces-panel__title">
          <span className="workplaces-panel__title-icon">{LoadIcon}</span>
          <div>
            <div className="workplaces-panel__heading">Workplaces</div>
            <div className="workplaces-panel__subtitle">
              Saved locally in this browser
            </div>
          </div>
        </div>
        <button
          type="button"
          className="workplaces-panel__new"
          onClick={handleCreate}
          disabled={busy || isCollaborating || !excalidrawAPI}
          title="New workplace"
        >
          {PlusIcon}
          <span>New</span>
        </button>
      </div>

      {isCollaborating && (
        <div className="workplaces-panel__banner">
          Leave the live session to switch workplaces.
        </div>
      )}

      {error && <div className="workplaces-panel__error">{error}</div>}

      <ul className="workplaces-panel__list">
        {workplaces.map((workplace) => {
          const isActive = workplace.id === activeId;
          const isEditing = editingId === workplace.id;

          return (
            <li key={workplace.id}>
              <div
                className={clsx("workplaces-panel__item", {
                  "is-active": isActive,
                  "is-disabled": isCollaborating,
                })}
                onClick={() => handleOpen(workplace)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleOpen(workplace);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="workplaces-panel__item-main">
                  {isEditing ? (
                    <input
                      className="workplaces-panel__rename-input"
                      value={draftName}
                      autoFocus
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => setDraftName(event.target.value)}
                      onBlur={() => commitRename(workplace.id)}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                        if (event.key === "Enter") {
                          commitRename(workplace.id);
                        } else if (event.key === "Escape") {
                          setEditingId(null);
                        }
                      }}
                    />
                  ) : (
                    <div className="workplaces-panel__item-name">
                      {workplace.name}
                      {isActive && (
                        <span className="workplaces-panel__badge">Open</span>
                      )}
                    </div>
                  )}
                  <div className="workplaces-panel__item-meta">
                    Updated {formatUpdatedAt(workplace.updatedAt)}
                  </div>
                </div>
                <div className="workplaces-panel__item-actions">
                  <button
                    type="button"
                    className="workplaces-panel__icon-btn"
                    title="Rename"
                    disabled={busy || isCollaborating}
                    onClick={(event) => startRename(workplace, event)}
                  >
                    {pencilIcon}
                  </button>
                  <button
                    type="button"
                    className="workplaces-panel__icon-btn workplaces-panel__icon-btn--danger"
                    title="Delete"
                    disabled={busy || isCollaborating || workplaces.length <= 1}
                    onClick={(event) => handleDelete(workplace, event)}
                  >
                    {TrashIcon}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {!workplaces.length && (
        <div className="workplaces-panel__empty">
          Your drawings will appear here as workplaces.
        </div>
      )}
    </div>
  );
};
